import { useEffect } from "react";
import { useDataChangeEffect, watchSlices } from "../../lib/useDataChangeEffect.js";
import {
  useCalendarsStore,
  useGoogleAuthStore,
  useGoogleSyncStore,
  useTasksStore,
} from "../../stores/index.js";

const DEBOUNCE_MS = 2500; // coalesce a burst of local edits into one push
const TICK_MS = 3 * 60 * 1000; // periodic pull of Google-side changes

/** Local changes that warrant an outbound push, watched IMPERATIVELY so a task
 *  edit doesn't re-render Shell — see `useDataChangeEffect`. */
const SYNCED_DATA = [
  watchSlices(useTasksStore, (s) => [s.tasks]),
  watchSlices(useCalendarsStore, (s) => [s.calendars]),
];

/**
 * Background Google Calendar sync. When connected AND at least one in-app
 * calendar is bound to a Google calendar, it reconciles:
 *  - shortly after any local task/calendar change (debounced — outbound push),
 *  - on a periodic tick + window focus + coming back online (inbound pull),
 *  - and once on mount/connect (boot).
 * All auto-runs are `silent`. `syncNow` is overlap-guarded and only rehydrates
 * on real changes, so it doesn't loop back through this effect. Mount once, in
 * Shell, next to `useSyncReconciler`.
 */
export function useGoogleSyncReconciler(): void {
  const connected = useGoogleAuthStore((s) => s.connected);
  const authLoaded = useGoogleAuthStore((s) => s.loaded);
  const authLoad = useGoogleAuthStore((s) => s.load);
  const syncNow = useGoogleSyncStore((s) => s.syncNow);

  // Calendars stay a render subscription: the binding gate needs their content,
  // and they change far too rarely to matter for edit latency.
  const hasBinding = useCalendarsStore((s) => s.calendars.some(
    (c) => c.external_source === "google" && c.sync_direction !== "off" && c.external_calendar_id,
  ));

  useEffect(() => {
    if (!authLoaded) void authLoad();
  }, [authLoaded, authLoad]);

  const enabled = connected && hasBinding;

  // Debounced sync on local change (and an initial run when first enabled).
  useDataChangeEffect(SYNCED_DATA, () => void syncNow({ silent: true }), {
    debounceMs: DEBOUNCE_MS,
    enabled,
    runOnEnable: true,
  });

  // Periodic pull + sync on focus/online (catches Google-side changes).
  useEffect(() => {
    if (!enabled) return;
    const run = () => void syncNow({ silent: true });
    const id = setInterval(run, TICK_MS);
    window.addEventListener("focus", run);
    window.addEventListener("online", run);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", run);
      window.removeEventListener("online", run);
    };
  }, [enabled, syncNow]);
}
